import { useEffect, useMemo, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Loader2 } from "lucide-react";

const PRODUCTION_LABELS = {
  nao_iniciado: "Não Iniciado", arte_final: "Arte Final", corte: "Corte",
  bordado: "Bordado", dtf: "DTF", sublimacao: "Sublimação",
  costura: "Costura", acabamento: "Acabamento", entregue: "Entregue",
};
const fmtDate = (iso) => { if (!iso) return "—"; const d = new Date(iso); return d.toLocaleDateString("pt-BR"); };
const brl = (v) => "R$ " + (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

const DEFAULT_OPTS = {
  title: true, customer: true, product: true, orderNo: true,
  qty: true, value: true, closedAt: true, dueDate: true, stage: true,
  sizeGrid: true, sizeTotal: true,
  header: true, footer: true,
  includePhotos: true,
};

export default function PrintOptionsModal({ item, onClose, onPrinted }) {
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [gallery, setGallery] = useState([]);
  const [selectedImgs, setSelectedImgs] = useState({}); // id -> bool
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!item?.opportunity_id) { setGallery([]); return; }
    setLoading(true);
    api.get(`/opportunities/${item.opportunity_id}`)
      .then(({ data }) => {
        const g = data?.gallery || [];
        setGallery(g);
        const sel = {}; g.forEach((im) => { sel[im.id] = true; });
        setSelectedImgs(sel);
      })
      .catch(() => setGallery([]))
      .finally(() => setLoading(false));
  }, [item?.opportunity_id]);

  const set = (k, v) => setOpts((o) => {
    const next = { ...o, [k]: v };
    // Se desmarcar a grade, também desmarcar o total (que fica disabled na UI)
    if (k === "sizeGrid" && !v) next.sizeTotal = false;
    return next;
  });
  const toggleImg = (id) => setSelectedImgs((s) => ({ ...s, [id]: !s[id] }));

  const chosenImgs = useMemo(() => gallery.filter((g) => selectedImgs[g.id]), [gallery, selectedImgs]);
  const totalPieces = (it) => (it.size_grid?.length ? it.size_grid.reduce((a, b) => a + (Number(b.qty) || 0), 0) : (it.quantity || 0));

  // Se desmarcar tudo, botão fica desabilitado
  const nothingSelected = useMemo(() => {
    // sizeTotal só conta se sizeGrid estiver marcado (já é ignorado na renderização quando disabled)
    const any = Object.entries(opts).some(([k, v]) => {
      if (k === "includePhotos") return false;
      if (k === "sizeTotal" && !opts.sizeGrid) return false;
      return !!v;
    });
    return !any && !(opts.includePhotos && chosenImgs.length > 0);
  }, [opts, chosenImgs]);

  const doPrint = async () => {
    if (nothingSelected || !item) return;
    setPrinting(true);
    try {
      const rows = (item.size_grid || []).map((r) => `<tr><td>${r.modelagem || "—"}</td><td>${r.tamanho || "—"}</td><td style="text-align:right">${r.qty || 0}</td></tr>`).join("");
      const gridTable = opts.sizeGrid
        ? (rows ? `<table class="grid"><thead><tr><th>Modelagem</th><th>Tamanho</th><th>Qtd</th></tr></thead><tbody>${rows}${opts.sizeTotal ? `<tr><td colspan="2" style="text-align:right;font-weight:700">Total</td><td style="text-align:right;font-weight:700">${totalPieces(item)}</td></tr>` : ""}</tbody></table>` : '<p class="muted">Sem grade de tamanhos informada.</p>')
        : "";

      const fields = [];
      if (opts.customer) fields.push(`<div class="field"><b>Cliente</b>${item.customer_name || "—"}</div>`);
      if (opts.product) fields.push(`<div class="field"><b>Produto</b>${item.product || "—"}</div>`);
      if (opts.stage) fields.push(`<div class="field"><b>Etapa de produção</b>${PRODUCTION_LABELS[item.stage] || item.stage}</div>`);
      if (opts.qty) fields.push(`<div class="field"><b>Quantidade total</b>${totalPieces(item)} peças</div>`);
      if (opts.value) fields.push(`<div class="field"><b>Valor</b>${brl(item.value)}</div>`);
      if (opts.closedAt) fields.push(`<div class="field"><b>Fechado em</b>${fmtDate(item.closed_at)}</div>`);
      if (opts.dueDate) fields.push(`<div class="field"><b>Prazo de entrega</b>${item.due_date ? fmtDate(item.due_date) : "—"}</div>`);
      if (opts.orderNo) fields.push(`<div class="field"><b>Nº do pedido</b>${(item.id || "").slice(0, 8)}</div>`);

      const photosHtml = (opts.includePhotos && chosenImgs.length > 0)
        ? `<h1 style="font-size:15px;margin-top:20px">Fotos do pedido</h1><div class="photos">${chosenImgs.map((g) => `<figure><img src="${mediaUrl(g.url)}" alt="${g.name || ""}"/><figcaption>${g.name || "Foto"}${g.type === "mockup" ? " · IA" : ""}</figcaption></figure>`).join("")}</div>`
        : "";

      const headerHtml = opts.header ? `
        <div class="head">
          <div class="brand-block">
            <img src="/atp-logo.webp" alt="ATP" class="logo"/>
            <div><div class="brand">ATP<span>.</span>Confecções</div><div style="font-size:12px;color:#666;margin-top:4px">Ordem de Produção</div></div>
          </div>
          <div class="company">ATP Confecções Ltda<br/>CNPJ: 38.581.693/0001-26<br/>Rua Valencio Manoel Floriano, 1125<br/>CEP 95560-000 — Torres/RS</div>
        </div>` : "";
      const footerHtml = opts.footer ? `<div class="foot">Documento gerado pelo ATP Gestão 360° em ${new Date().toLocaleString("pt-BR")}</div>` : "";

      const w = window.open("", "_blank", "width=820,height=1000");
      if (!w) { toast.error("Permita pop-ups para imprimir"); setPrinting(false); return; }
      w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Pedido — ${item.title || ""}</title>
        <style>
          *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;padding:32px}
          .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0CDBF2;padding-bottom:16px;margin-bottom:24px;gap:16px}
          .brand-block{display:flex;align-items:center;gap:12px}
          .logo{width:56px;height:56px;object-fit:contain}
          .brand{font-size:26px;font-weight:800;letter-spacing:-.5px}
          .brand span{color:#0CDBF2}
          .company{font-size:11px;color:#555;line-height:1.5;text-align:right}
          h1{font-size:18px;margin:0 0 16px}
          .grid2{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin-bottom:20px}
          .field{font-size:13px;padding:6px 0;border-bottom:1px solid #eee}
          .field b{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#888;margin-bottom:2px}
          table.grid{width:100%;border-collapse:collapse;margin-top:8px}
          table.grid th,table.grid td{border:1px solid #ddd;padding:6px 10px;font-size:13px}
          table.grid th{background:#f4f4f4;text-align:left}
          .muted{color:#888;font-size:13px}
          .photos{display:grid;grid-template-columns:1fr 1fr;gap:16px}
          .photos figure{margin:0;break-inside:avoid;page-break-inside:avoid}
          .photos img{width:100%;height:8cm;object-fit:contain;border:1px solid #eee;background:#fafafa}
          .photos figcaption{font-size:11px;color:#555;margin-top:4px;text-align:center}
          .foot{margin-top:40px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px}
          @media print { @page { margin: 14mm } }
        </style></head><body>
        ${headerHtml}
        ${opts.title ? `<h1>${item.title || "Pedido"}</h1>` : ""}
        ${fields.length ? `<div class="grid2">${fields.join("")}</div>` : ""}
        ${opts.sizeGrid ? `<h1 style="font-size:15px">Grade de tamanhos</h1>${gridTable}` : ""}
        ${photosHtml}
        ${footerHtml}
        </body></html>`);
      w.document.close(); w.focus();

      // Register print on backend
      try {
        await api.post(`/production/${item.id}/print`);
        onPrinted && onPrinted();
      } catch (e) { /* not fatal */ }

      setTimeout(() => { try { w.print(); } catch (_) {} }, 500);
      onClose();
    } finally {
      setPrinting(false);
    }
  };

  const Row = ({ k, label, disabled }) => (
    <label className={`flex items-center gap-2 py-1 text-sm ${disabled ? "opacity-40" : "cursor-pointer"}`}>
      <Checkbox checked={!!opts[k]} disabled={disabled} onCheckedChange={(v) => set(k, !!v)} data-testid={`print-opt-${k}`} />
      <span>{label}</span>
    </label>
  );

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="print-options-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Opções de impressão</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <section>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Identificação</div>
            <Row k="title" label="Título do pedido" />
            <Row k="customer" label="Nome do cliente" />
            <Row k="product" label="Produto" />
            <Row k="orderNo" label="Nº do pedido" />
          </section>
          <section>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Números</div>
            <Row k="qty" label="Quantidade total de peças" />
            <Row k="value" label="Valor do pedido" />
            <Row k="closedAt" label="Data de fechamento" />
            <Row k="dueDate" label="Prazo de entrega" />
            <Row k="stage" label="Etapa de produção atual" />
          </section>
          <section>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Grade de tamanhos</div>
            <Row k="sizeGrid" label="Tabela Modelagem × Tamanho × Qtd" />
            <Row k="sizeTotal" label="Linha de total de peças" disabled={!opts.sizeGrid} />
          </section>
          <section>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Cabeçalho e rodapé</div>
            <Row k="header" label="Cabeçalho ATP (logo + CNPJ + endereço)" />
            <Row k="footer" label="Rodapé “gerado pelo ATP Gestão 360°”" />
          </section>
        </div>

        <section className="border-t border-slate-800 pt-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Fotos</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={opts.includePhotos} onCheckedChange={(v) => set("includePhotos", !!v)} data-testid="print-opt-includePhotos" />
              <span>Incluir fotos do pedido</span>
            </label>
          </div>
          {loading ? (
            <div className="mt-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>Carregando fotos…</div>
          ) : gallery.length === 0 ? (
            <div className="mt-2 rounded-md border border-slate-800 bg-[#111622] p-3 text-sm text-slate-500" data-testid="print-no-photos">Este pedido não tem fotos anexadas.</div>
          ) : (
            <div className={`mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2 ${opts.includePhotos ? "" : "opacity-40 pointer-events-none"}`}>
              {gallery.map((g) => (
                <label key={g.id} className="relative block cursor-pointer" data-testid={`print-img-${g.id}`}>
                  <img src={mediaUrl(g.url)} alt={g.name} className={`w-full h-16 object-cover rounded border ${selectedImgs[g.id] ? "border-[#0CDBF2]" : "border-slate-700 opacity-60"}`} />
                  <input type="checkbox" checked={!!selectedImgs[g.id]} onChange={() => toggleImg(g.id)} className="absolute top-1 left-1 accent-[#0CDBF2]" />
                  {g.type === "mockup" && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-[#0CDBF2]/80 text-[#07090E] font-semibold">IA</span>}
                </label>
              ))}
            </div>
          )}
        </section>

        <DialogFooter className="pt-3 border-t border-slate-800">
          <Button variant="outline" onClick={onClose} className="border-slate-700 hover:bg-slate-800" data-testid="print-cancel">Cancelar</Button>
          <Button onClick={doPrint} disabled={nothingSelected || printing} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="print-confirm">
            {printing ? <Loader2 size={14} className="animate-spin mr-2"/> : <Printer size={14} className="mr-2"/>}Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
